// hooks
import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";

// material styles
import { Grid, Button, TextField } from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// request methods
import { getaboutUsRequest, updateAboutUsRequest } from "api/requests/aboutus";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import ProgressBarCM from "components/shared/ProgressBar";
import Upload from "components/shared/Upload";
import UploadVideoCM from "components/shared/Video/Upload";

// helper
import Toast from "helper/toast";

// form validate
import { aboutUsSchema, validateImg } from "validations";

// styled component
import { StyledStack, StyledTitle, ButtonContainer } from "./About.style";

// editor library
import Editor, { buttonList } from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";

export default function AboutUs() {
  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(aboutUsSchema),
  });

  // editor text
  const [text, setText] = useState("");

  // upload image and video state
  const [aboutUsImage, setAboutUsImage] = useState({
    url: null,
    isChanged: false,
  });
  const [aboutUsVideo, setAboutUsVideo] = useState({
    url: null,
    isChanged: false,
  });

  // editor text handler method
  const handleChangeText = (text) => {
    setText(text);
  };

  // updated about us
  const onSubmit = async (data) => {
    if (!text) return Toast({ type: "empty", title: "فیلد توضیحات " });
    if (!aboutUsImage.url) return Toast({ type: "empty", title: "فیلد عکس " });

    const res = await request({
      url: () =>
        updateAboutUsRequest(
          {
            text: text,
            phone_number1: data.phone_number_one,
            phone_number2: data.phone_number_two,
            alt: data.alt,
            keyword: data.keyword,
            meta_description: data.meta_description,
            address: data.address,
            whatsapp: data.whatsapp,
            email: data.email,
            telegram: data.telegram,
            instagram: data.instagram,
            main_image: aboutUsImage,
            video: aboutUsVideo,
          },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });
    if (res) {
      Toast({ type: "success", title: "ویرایش درباره ما " });
      getAboutUs();
    }
  };
  // get about us data
  const getAboutUs = useCallback(async () => {
    setLoading(true);

    const res = await request({
      url: () => getaboutUsRequest(),
    });
    if (res) {
      res.data.text && setText(res.data.text);
      let data = res.data;
      reset({
        address: data.address,
        phone_number_one: data.phone_number1,
        phone_number_two: data.phone_number2,
        whatsapp: data.whatsapp,
        telegram: data.telegram,
        instagram: data.instagram,
        email: data.email,
        alt: data?.main_image?.alt,
        keyword: data?.seo_options?.keyword,
        meta_description: data?.seo_options?.meta_description,
      });
      setAboutUsImage({ url: data?.main_image?.url, isWeb: true });
      setAboutUsVideo({ url: data?.video, isWeb: true });
    }
  }, [request, reset, setLoading]);
  useEffect(() => {
    getAboutUs();
  }, [getAboutUs]);

  // uploaded image handler method
  const handleChangeAboutUsImage = (val) => {
    validateImg(val);
    setAboutUsImage({ url: val, isChanged: true });
  };

  // uploaded video handler method
  const handleChangeAboutUsVideo = (val) =>
    setAboutUsVideo({ url: val, isChanged: true });

  return (
    <DrawerCM title="تماس با ما">
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <Grid container>
          <StyledStack>
            <StyledTitle>افزودن اطلاعات تماس با ما</StyledTitle>
            <Grid container>
              <Grid item md={6} xs={12} p={1.2}>
                {aboutUsImage.url ? (
                  <Upload
                    isWeb={aboutUsImage.isWeb}
                    isLocal={!aboutUsImage.isWeb}
                    file={aboutUsImage.url}
                    refillable
                    type="add-edit-about-us"
                    onChange={handleChangeAboutUsImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                ) : (
                  <Upload
                    isLocal={aboutUsImage.isChanged}
                    file={aboutUsImage.url}
                    refillable
                    type="add-add-about-us"
                    onChange={handleChangeAboutUsImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {aboutUsVideo.url ? (
                  <UploadVideoCM
                    isWeb={aboutUsVideo.isWeb}
                    isLocal={!aboutUsVideo.isWeb}
                    file={aboutUsVideo.url}
                    refillable
                    type="add-edit-video"
                    onChange={handleChangeAboutUsVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                ) : (
                  <UploadVideoCM
                    isLocal={aboutUsVideo.isChanged}
                    file={aboutUsVideo.url}
                    refillable
                    type="add-add-video"
                    onChange={handleChangeAboutUsVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label=" متن جایگزین"
                  variant="outlined"
                  fullWidth
                  id="alt"
                  {...register("alt")}
                  error={Boolean(errors.alt)}
                  helperText={errors.alt?.message}
                />
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label="کلمه کلیدی"
                  variant="outlined"
                  fullWidth
                  id="keyword"
                  {...register("keyword")}
                  error={Boolean(errors.keyword)}
                  helperText={errors.keyword?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                <TextField
                  label="فراتوضیحات"
                  variant="outlined"
                  fullWidth
                  id="meta_description"
                  {...register("meta_description")}
                  error={Boolean(errors.meta_description)}
                  helperText={errors.meta_description?.message}
                />
              </Grid>

              <Grid item xs={12} p={1.2}>
                {text && (
                  <Editor
                    enableToolbar={true}
                    onChange={handleChangeText}
                    defaultValue={text}
                    setDefaultStyle="font-family: IranSans; height: 40vh;"
                    setOptions={{
                      buttonList: buttonList.complex,
                      font: [
                        "IranSans",
                        "IranSansBold",
                        "Arial",
                        "Impact",
                        "Tahoma",
                      ],
                    }}
                  />
                )}
              </Grid>

              <Grid item md={4} xs={12} p={1.5}>
                <TextField
                  label="شماره تماس 1"
                  variant="outlined"
                  fullWidth
                  id="phone_number_one"
                  {...register("phone_number_one")}
                  error={Boolean(errors.phone_number_one)}
                  helperText={errors.phone_number_one?.message}
                />
              </Grid>
              <Grid item md={4} xs={12} p={1.5}>
                <TextField
                  label="شماره تماس 2"
                  variant="outlined"
                  fullWidth
                  id="phone_number_two"
                  {...register("phone_number_two")}
                  error={Boolean(errors.phone_number_two)}
                  helperText={errors.phone_number_two?.message}
                />
              </Grid>
              <Grid item md={4} xs={12} p={1.5}>
                <TextField
                  label="ایمیل"
                  variant="outlined"
                  fullWidth
                  id="email"
                  {...register("email")}
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                />
              </Grid>

              <Grid item md={6} xs={12} p={1.5}>
                <TextField
                  label="آدرس اینستاگرام"
                  variant="outlined"
                  fullWidth
                  id="instagram"
                  {...register("instagram")}
                  error={Boolean(errors.instagram)}
                  helperText={errors.instagram?.message}
                />
              </Grid>
              <Grid item md={6} xs={12} p={1.5}>
                <TextField
                  label="آدرس تلگرام"
                  variant="outlined"
                  fullWidth
                  id="telegram"
                  {...register("telegram")}
                  error={Boolean(errors.telegram)}
                  helperText={errors.telegram?.message}
                />
              </Grid>
              <Grid item md={6} xs={12} p={1.5}>
                <TextField
                  label="آدرس واتساپ"
                  variant="outlined"
                  fullWidth
                  id="whatsapp"
                  {...register("whatsapp")}
                  error={Boolean(errors.whatsapp)}
                  helperText={errors.whatsapp?.message}
                />
              </Grid>
              <Grid item md={6} xs={12} p={1.5}>
                <TextField
                  label="آدرس"
                  variant="outlined"
                  fullWidth
                  id="address"
                  {...register("address")}
                  error={Boolean(errors.address)}
                  helperText={errors.address?.message}
                />
              </Grid>
              <ButtonContainer>
                <Button
                  startIcon={<CheckIcon />}
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ذخیره
                </Button>
              </ButtonContainer>
            </Grid>
          </StyledStack>
        </Grid>
      )}
    </DrawerCM>
  );
}
